import { MouseBlocker } from "@lib/titan2/dist/game/control/MouseBlocker";
import { TitanSpine } from "@lib/titan2/dist/game/control/TitanSpine";
import { CommonGameEvent } from "@lib/titan2/dist/game/controller/event/CommonGameEvent";
import { OrientationType } from "@lib/titan2/dist/game/renderer/constant/RendererConstants";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { SpineUtil } from "@lib/titan2/dist/util/view/SpineUtil";
import { ViewUtils } from "@lib/titan2/dist/util/view/ViewUtils";
import { Linear, gsap } from "gsap";
import { SlingoEvent } from "../../../controller/event/SlingoEvent";
import { TweenConstants } from "../constant/TweenConstants";
import { SlingoStyle } from "../style/SlingoStyle";
import { AbstractGameView } from "./AbstractGameView";
/**
 * ScatterWin.ts
 */
export class ScatterWin extends AbstractGameView {
    createProperties(properties, portraitProperties) {
        super.createProperties(properties, portraitProperties);
        this.landscapeProperties = properties;
        this.portraitProperties = portraitProperties;
        this.uiProperties = this.uiProperties;
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(CommonGameEvent.PURCHASE_ENTRY, this.reset);
        this.registerGame(SlingoEvent.SCATTER_WIN_ANIMATION, this.handlePlayScatterWin);
    }
    create() {
        super.create();
        this.createMouseBlocker();
        this.animation = TitanSpine.createSpine(this, this.landscapeProperties.animationProperties, this.portraitProperties?.animationProperties);
        this.titleContainer = ViewUtils.createContainer(this, "titleContainer");
        this.title0 = ViewUtils.createField(this.titleContainer, this.landscapeProperties.title0Properties, this.portraitProperties?.title0Properties);
        this.title1 = ViewUtils.createField(this.titleContainer, this.landscapeProperties.title1Properties, this.portraitProperties?.title1Properties);
        this.counter = ViewUtils.createField(this.titleContainer, this.landscapeProperties.counterProperties, this.portraitProperties?.counterProperties);
    }
    createMouseBlocker() {
        if (this.uiProperties.mouseBlockerProperties) {
            this.mouseBlocker = new MouseBlocker(this.landscapeProperties.mouseBlockerProperties, this.portraitProperties?.mouseBlockerProperties);
        }
    }
    showOverlay() {
        if (this.mouseBlocker) {
            this.mouseBlocker.alpha = 0;
            this.addChildAt(this.mouseBlocker, 0);
            gsap.to(this.mouseBlocker, { duration: TweenConstants.DEFAULT_OVERLAY_FADE_DURATION, alpha: 1 });
        }
    }
    hideOverlay() {
        this.mouseBlocker && gsap.to(this.mouseBlocker, {
            duration: TweenConstants.DEFAULT_OVERLAY_FADE_DURATION,
            alpha: 0,
            onComplete: () => {
                this.removeChild(this.mouseBlocker);
            }
        });
    }
    postCreate() {
        super.postCreate();
        this.reset();
        Logger.addObjectRef(this, "ScatterWin");
    }
    reset() {
        SpineUtil.reset(this.animation);
        this.titleContainer.scale.set(0, 0);
        this.counterValue = 0;
        this.counter.setText("");
    }
    handlePlayScatterWin() {
        const landscape = this.renderer.getOrientation() === OrientationType.LANDSCAPE;
        this.showTitle();
        this.dispatchGame(SlingoEvent.SCATTER_WIN_APPEAR);
        this.showOverlay();
        SpineUtil.play(this.animation, landscape ? SlingoStyle.SCATTER_WIN_IN_LANDSCAPE : SlingoStyle.SCATTER_WIN_IN_PORTRAIT, () => {
            this.animation.play(landscape ? SlingoStyle.SCATTER_WIN_LOOP_LANDSCAPE : SlingoStyle.SCATTER_WIN_LOOP_PORTRAIT, true);
            const payoutConfig = this.model.getPayoutConfig();
            const currentStake = this.model.getCurrentStake();
            const symbolPayouts = payoutConfig.getSymbolPayouts().getValue(undefined).getMultipliers();
            const result = this.model.getSpinResult();
            let symbolWinIndex = 0;
            if (result?.hasSymbolWin() && result.getSymbolWins()[0].getNumberOfSymbols() >= 3) {
                symbolWinIndex = result.getSymbolWins()[0].getNumberOfSymbols() - 1;
            }
            this.counter.setText(this.formatCurrency(0));
            this.counter.visible = true;
            this.dispatchGame(SlingoEvent.SCATTER_WIN_COUNT);
            gsap.to(this, {
                counterValue: currentStake * symbolPayouts[symbolWinIndex],
                duration: this.uiProperties.counterTimes[symbolWinIndex],
                ease: Linear.easeNone,
                onUpdate: () => {
                    this.counter.setText(this.formatCurrency(this.counterValue));
                },
                onComplete: () => {
                    this.dispatchGame(SlingoEvent.SCATTER_WIN_COUNT_END);
                    gsap.timeline()
                        .to(this.counter.scale, { x: 1.1, y: 1.1, duration: TweenConstants.YOYO_DURATION, yoyo: true, repeat: 1 })
                        .call(() => {
                        this.hideTitle();
                        this.dispatchGame(SlingoEvent.SCATTER_WIN_DISAPPEAR);
                        SpineUtil.play(this.animation, landscape ? SlingoStyle.SCATTER_WIN_OUT_LANDSCAPE : SlingoStyle.SCATTER_WIN_OUT_PORTRAIT, this.handleAnimationComplete, this);
                    });
                }
            });
        });
    }
    showTitle() {
        gsap.to(this.titleContainer.scale, { x: 1, y: 1, duration: TweenConstants.SCATTER_WIN_TITLE_SCALE_DURATION });
    }
    hideTitle() {
        gsap.timeline()
            .to(this.titleContainer.scale, { x: 0, y: 0, duration: TweenConstants.SCATTER_WIN_TITLE_SCALE_DURATION })
            .call(() => this.hideOverlay());
    }
    handleAnimationComplete() {
        this.reset();
    }
    isPlaying() {
        return this.animation.visible;
    }
    handleRotate(event) {
        super.handleRotate(event);
        this.uiProperties = this.uiProperties;
        if (!this.isPlaying()) {
            this.titleContainer.scale.set(0, 0);
        }
        this.titleContainer.position.set(this.uiProperties.titlePosition.x, this.uiProperties.titlePosition.y);
    }
    handleResize(event) {
        super.handleResize(event);
        if (this.parent) {
            const rendererSize = this.renderer.getGameSize();
            const landscape = this.renderer.getOrientation() === OrientationType.LANDSCAPE;
            const newScale = landscape ? rendererSize.y / this.uiProperties.size.y : rendererSize.x / this.uiProperties.size.x;
            this.scale.set(newScale, newScale);
        }
    }
}
//# sourceMappingURL=ScatterWin.js.map